import { useState, type ReactNode } from 'react';
import type { ProseInputKind } from '@ni/shared';
import { usePersonRecord } from './record';
import { OVERVIEW_ASIDE, PROSE_INPUT_KIND_LABELS } from '../../lib/copy';
import { formatDay, humanize, joinClauses } from '../../lib/format';
import { noteRailLines } from '../../lib/note-rail';
import type { NoteLine } from '../../components/record/notes-list';
import { Modal } from '../../components/ui/modal';
import { EmptyLine } from '../../components/ui/empty-line';
import { RecordProps } from '../../components/record/record-props';
import { Button } from '../../components/ui/button';
import { NoteChangeActions } from '../../components/record/note-change';
import { useMayWrite } from '../../api/app-context';
import { axisOfWrite } from '../../api/client';
import './overview.css';

function proseOf(line: NoteLine): string {
  return line.pieces ? line.pieces.map((piece) => piece.prose).join('\n\n') : line.prose;
}

/* Past either bound a note shows its opening and "Read full note" opens the rest. */
const EXCERPT_CHARS = 600;
const EXCERPT_LINES = 8;

/** The opening of a long note, or null when the whole note fits. */
export function excerptOf(prose: string): string | null {
  const lines = prose.split('\n');
  if (prose.length <= EXCERPT_CHARS && lines.length <= EXCERPT_LINES) return null;
  const kept = lines.slice(0, EXCERPT_LINES).join('\n');
  let head = kept.slice(0, EXCERPT_CHARS);
  /* A cut inside a word ends on the word before it, unless the cut sits in one
     very long run of text. */
  if (/\S/.test(kept.charAt(EXCERPT_CHARS)) && /\S$/.test(head)) {
    const space = head.search(/\s\S*$/);
    if (space > EXCERPT_CHARS / 2) head = head.slice(0, space);
  }
  return `${head.trimEnd()}…`;
}

/** The words as typed, one block per paragraph (a blank line); single breaks stay. */
function NoteProse({ prose }: { prose: string }) {
  return (
    <div className="person-note-prose">
      {prose
        .split(/\n\s*\n/)
        .filter((paragraph) => paragraph.trim() !== '')
        .map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
    </div>
  );
}

/** The date, then whoever wrote it and any marks ("confidential"), on one quiet line. */
function NoteMeta({ line }: { line: NoteLine }) {
  return (
    <div className="person-note-meta">
      {line.date ? <span className="num">{line.date}</span> : null}
      {line.date && line.note ? <span className="sep">·</span> : null}
      {line.note ? <span className="who">{line.note}</span> : null}
    </div>
  );
}

function NoteBody({ line }: { line: NoteLine }) {
  return (
    <>
      {line.pieces ? (
        line.pieces.map((piece) => (
          <div key={piece.key} className="person-note-piece">
            <NoteProse prose={piece.prose} />
            {piece.note && <div className="src">{piece.note}</div>}
          </div>
        ))
      ) : (
        <NoteProse prose={line.prose} />
      )}
    </>
  );
}

export function Overview() {
  const { record, justAdded } = usePersonRecord();
  const mayChangeNotes = useMayWrite(axisOfWrite('editInput'));
  const notes = noteRailLines(record.notes);
  const known = new Set(record.interactions.map((entry) => entry.id));
  const interactions: NoteLine[] = [
    ...(justAdded?.created.interactions ?? []).filter((entry) => !known.has(entry.id)),
    ...record.interactions,
  ]
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
    .map((entry) => ({
      key: entry.id,
      date: formatDay(entry.occurred_at),
      prose: entry.prose,
      note: entry.participants?.join(' · ') || null,
    }));
  const fields = Object.entries(record.subject.record_fields).flatMap(([key, raw]) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !('value' in raw)) return [];
    const field = raw as Record<string, unknown>;
    if (field.value === null || field.value === undefined) return [];
    const kind =
      typeof field.source === 'string' && field.source in PROSE_INPUT_KIND_LABELS
        ? PROSE_INPUT_KIND_LABELS[field.source as ProseInputKind]
        : null;
    return [
      {
        key,
        value: typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value),
        source: notes.find(
          (note) =>
            note.key === field.prose_input_id ||
            note.pieces?.some((piece) => piece.key === field.prose_input_id),
        ),
        attribution: joinClauses([
          kind,
          typeof field.recorded_at === 'string' ? formatDay(field.recorded_at) : null,
        ]),
      },
    ];
  });
  return (
    <RecordOverview
      notes={notes}
      interactions={interactions}
      fields={fields}
      noteActions={
        mayChangeNotes
          ? (line, prose) =>
              line.inputId ? (
                <NoteChangeActions
                  subjectId={record.subject.id}
                  inputId={line.inputId}
                  prose={prose}
                />
              ) : null
          : undefined
      }
      about={
        <RecordProps record={record}>
          <dt>{OVERVIEW_ASIDE.added}</dt>
          <dd className="num">{formatDay(record.subject.created_at)}</dd>
        </RecordProps>
      }
    />
  );
}

export interface RecordedDetail {
  key: string;
  value: string;
  source?: NoteLine;
  attribution: string;
}
/** Shared record overview. Callers supply stored notes and details, never synthetic facts. */
export function RecordOverview({
  notes,
  interactions,
  fields,
  about,
  noteActions,
}: {
  notes: NoteLine[];
  interactions: NoteLine[];
  fields: RecordedDetail[];
  /** The record's own properties, above its recorded details. */
  about?: ReactNode;
  /** Controls beside a note's meta line, where the surface offers any. */
  noteActions?: (line: NoteLine, prose: string) => ReactNode;
}) {
  const [view, setView] = useState<'notes' | 'interactions'>('notes');
  const [reader, setReader] = useState<NoteLine | null>(null);
  const entries = view === 'notes' ? notes : interactions;
  return (
    <div className="person-overview">
      <div className="person-notes">
        <div className="person-note-switch" role="group" aria-label="Show notes or interactions">
          <button type="button" aria-pressed={view === 'notes'} onClick={() => setView('notes')}>
            Notes <span className="c">{notes.length}</span>
          </button>
          <button
            type="button"
            aria-pressed={view === 'interactions'}
            onClick={() => setView('interactions')}
          >
            Interactions <span className="c">{interactions.length}</span>
          </button>
        </div>
        <section
          className="person-note-list"
          aria-label={view === 'notes' ? 'Notes' : 'Interactions'}
        >
          {entries.length === 0 && (
            <EmptyLine>
              {view === 'notes' ? 'No notes yet.' : 'No interactions recorded.'}
            </EmptyLine>
          )}
          {entries.map((line) => {
            const excerpt = excerptOf(proseOf(line));
            return (
              <article className="person-note" key={line.key}>
                <div className="person-note-head">
                  <NoteMeta line={line} />
                  {view === 'notes' && noteActions ? noteActions(line, proseOf(line)) : null}
                </div>
                {excerpt !== null ? (
                  <>
                    <NoteProse prose={excerpt} />
                    <button type="button" className="text-link" onClick={() => setReader(line)}>
                      Read full {view === 'notes' ? 'note' : 'interaction'}
                    </button>
                  </>
                ) : (
                  <NoteBody line={line} />
                )}
              </article>
            );
          })}
        </section>
      </div>
      <aside className="person-aside record-aside" aria-label="About this record">
        {about ? <div className="props-block">{about}</div> : null}
        <div className="person-facts">
          <h2 className="person-facts-heading">
            Recorded details <span className="c">{fields.length}</span>
          </h2>
          <div className="person-facts-list">
            {fields.length === 0 && <EmptyLine>No details recorded.</EmptyLine>}
            {fields.map((field) => (
              <div className="person-fact" key={field.key}>
                <div className="k">{humanize(field.key)}</div>
                <div className="person-fact-value">{field.value}</div>
                {field.source ? (
                  <button
                    type="button"
                    className="text-link"
                    aria-label={`Source for ${humanize(field.key)}`}
                    onClick={() => setReader(field.source!)}
                  >
                    Source
                  </button>
                ) : (
                  field.attribution && <div className="src">{field.attribution}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>
      {reader && (
        <Modal label="Original note" wide onClose={() => setReader(null)}>
          <div className="modal-head">{reader.date || 'Original note'}</div>
          {reader.note && <div className="person-note-meta">{reader.note}</div>}
          <div className="modal-body person-note-reader">
            <NoteBody line={reader} />
          </div>
          <div className="modal-actions">
            <Button variant="act" onClick={() => setReader(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
